import {
  Fragment, memo, useCallback, useContext, useEffect, useState, type FunctionComponent, type ReactNode
} from 'react';
import pick from 'lodash/pick';
import map from 'lodash/map';
import size from 'lodash/size';
import findIndex from 'lodash/findIndex';
import isNull from 'lodash/isNull';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import clsx from 'clsx';
import { useApolloClient } from '@apollo/client';
// Material UI imports
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
// Skillmore UI Components
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import InlineAlert from '@empathco/ui-components/src/elements/InlineAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import CardDeck from '@empathco/ui-components/src/elements/CardDeck';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import ReloadingOverlay from '@empathco/ui-components/src/elements/ReloadingOverlay';
// local imports
import { SkillUpdateSource } from '../constants/mixpanel';
import { Skill, SkillLevel } from '../models/skill';
import useCustomerSettings from '../config/customer';
import { PREF_SKILL_FIELDS /* , TARGET_SKILL_FIELDS, updateCachedIsTarget */ } from '../helpers/models';
import { SkillSort } from '../constants/skillsSort';
import { DataContext } from '../context';
import { hasPagination, PaginationControlsComponent } from '../v3/PaginationControls';
import ComputeUpdatesOverlay from '../v3/ComputeUpdatesOverlay';
import SkillCard from '../v3/SkillCard';
import SkillsTable from '../v3/SkillsTable';
import SkillLevelDialog from '../widgets/SkillLevelDialog';
// SCSS imports
import { topPadding, bottomPadding, shadyBg, dirtyBox } from '@empathco/ui-components/src/styles/modules/ItemsGrid.module.scss';

type RemoveIconType = 'inferred' | 'all';

type SkillsGridProps = {
  readonly dirty?: boolean | null;
  readonly skills?: Skill[] | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly filters?: ReactNode | null;
  readonly pagination?: ReactNode | null;
  readonly onItemUpdate?: ((id: number, level: SkillLevel, mentoring: boolean, targeting: boolean) => void) | null;
  readonly onItemRemove?: ((skill: Skill) => void) | null;
  readonly shady?: boolean | null;
  readonly disabled?: boolean | null;
  readonly supervisor?: boolean; // only used in table view
  readonly isEmployee?: boolean;
  readonly plain?: boolean;
  readonly small?: boolean;
  readonly readOnly?: boolean;
  readonly manualOnly?: boolean;
  readonly depersonalised?: boolean;
  readonly withLink?: boolean; // if `false`, enables skill level dialog even when `isEmployee` is `false`
  readonly withExpectedLevel?: boolean;
  readonly withCourses?: boolean;
  readonly withRemoveSuggested?: boolean;
  readonly removeIcon?: RemoveIconType;
  readonly withReloading?: boolean;
  readonly narrowGrid?: boolean;
  // readonly breakAfter?: number | null;
  readonly notFoundMessage?: string | null; // null => don't show message, undefined => show default message
  readonly table?: boolean | null;
  readonly sortBy?: SkillSort | null;
  readonly direction?: boolean | null;
  readonly changeSort?: ((sort: SkillSort, direction: boolean) => void);
  readonly sortDisabled?: boolean | null;
  readonly source?: SkillUpdateSource;
  readonly sourceId?: number;
  readonly updatingSkillId?: number | null;
  // non-empty => a manager is editing this employee's skill levels
  readonly selectedEmployeeId?: string | null;
  // display name of that employee, shown in the skill level dialog
  readonly selectedEmployeeName?: string | null;
  // for Storybook only: pretend this grid is the one that issued the update currently failing
  readonly testSaved?: boolean;
}

const SkillsGrid: FunctionComponent<SkillsGridProps> = ({
  skills,
  pending = false,
  failed = false,
  dirty = false,
  disabled: parentDisabled = false,
  onItemUpdate,
  onItemRemove,
  shady = false,
  supervisor = false,
  isEmployee = false,
  plain = false,
  small = false,
  readOnly = false,
  manualOnly = false,
  depersonalised = false,
  withLink,
  withExpectedLevel = false,
  withCourses = false,
  withRemoveSuggested = false,
  removeIcon,
  withReloading = false,
  narrowGrid = false,
  // breakAfter = 0,
  notFoundMessage,
  filters,
  pagination,
  table = false,
  sortBy,
  direction,
  changeSort,
  sortDisabled = false,
  source,
  sourceId,
  updatingSkillId,
  selectedEmployeeId,
  selectedEmployeeName,
  testSaved = false
  // eslint-disable-next-line complexity, max-lines-per-function
}) => {
  const { cache } = useApolloClient();
  const { HAS_MENTORING } = useCustomerSettings();

  const {
    skillUpdate: { pending: updatePending, failed: updateFailed, params: updateParams }, updateSkill
    // targetSkillUpdate: { pending: targetPending, failed: targetFailed, params: targetParams }, updateTargetSkill
  } = useContext(DataContext);

  const editable = !isEmpty(selectedEmployeeId);

  const withPagination = hasPagination(pagination as PaginationControlsComponent);
  const loading = withReloading ? pending && !skills : pending || !skills;
  const reloading = Boolean(withReloading && pending && skills);

  const [open, setOpen] = useState(false);
  const [skill, setSkill] = useState<Skill | null>(null);
  // the skill this grid last asked to save — see `ownsFailedUpdate` below
  const [savedSkillId, setSavedSkillId] = useState<number | null>(null);

  useEffect(() => {
    if (!updatePending) setSavedSkillId(null);
  }, [updatePending]);

  const handleClick = useCallback((skl: Skill) => {
    setOpen(true);
    setSkill(skl);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleExited = useCallback(() => {
    setSkill(null);
  }, []);

  const handleLevelChange = useCallback((level: SkillLevel, is_opt_in_mentor: boolean, is_target: boolean) => {
    if (skill) {
      if (onItemUpdate) onItemUpdate(skill.id, level, is_opt_in_mentor, is_target);
      else if (source) {
        setSavedSkillId(skill.id);
        updateSkill?.({
          apolloCache: cache,
          skill_id: skill.id,
          level,
          // a manager may only change the level: mentoring / targeting stay the employee's own
          ...editable
            ? { selected_employee_id: selectedEmployeeId }
            : { ...HAS_MENTORING ? { is_opt_in_mentor } : {}, is_target },
          source,
          source_id: sourceId,
          skill: pick(skill, PREF_SKILL_FIELDS)
        });
      }
    }
    setOpen(false);
  }, [onItemUpdate, skill, source, sourceId, updateSkill, cache, HAS_MENTORING, editable, selectedEmployeeId]);

  const handleRemove = useCallback((skl: Skill) => {
    if (skl) {
      if (onItemRemove) onItemRemove(skl);
      else if (onItemUpdate) onItemUpdate(skl.id, 0, false, false);
      else if (source) {
        setSavedSkillId(skl.id);
        updateSkill?.({
          apolloCache: cache,
          skill_id: skl.id,
          level: 0,
          // a manager may only change the level: mentoring / targeting stay the employee's own
          ...editable
            ? { selected_employee_id: selectedEmployeeId }
            : { ...HAS_MENTORING ? { is_opt_in_mentor: false } : {}, is_target: false },
          source,
          source_id: sourceId,
          skill: pick(skl, PREF_SKILL_FIELDS)
        });
      }
    }
  }, [
    source, sourceId, onItemUpdate, onItemRemove, updateSkill, cache, HAS_MENTORING, editable, selectedEmployeeId
  ]);

  // const handleTargetClick = useCallback((skl: Skill) => {
  //   if (skl && skl.id) updateTargetSkill?.({
  //     skill_id: skl.id,
  //     is_target: !skl.is_target,
  //     targetSkill: pick(updateCachedIsTarget(skl, !skl.is_target), TARGET_SKILL_FIELDS)
  //   });
  // }, [updateTargetSkill]);

  const updatingId = updatingSkillId || (updatePending && updateParams ? updateParams.skill_id : null);
  // const updatingTargetId = targetPending && targetParams ? targetParams.skill_id : null;

  const disabled = parentDisabled || updatePending; // || targetPending;

  // `updateFailed` / `updateParams` are global to the screen but the snackbar below is per-grid, so this grid may
  // only report a save it issued itself. With `onItemUpdate` it issues none: every level change and removal goes to
  // the parent, which saves them alongside saves this grid never sees at all (`BuilderInferredStep`'s Add Skill
  // dialog) and reports its own failures. Without it, an employee's screen shows a given skill in one savable grid,
  // so "the failed skill is in my list" is enough; `SupervisorEmployeeScreen` mounts nine editable grids whose
  // lists overlap, so in `editable` mode the grid that issued the save owns the error and the rest stay quiet
  const ownsFailedUpdate = !onItemUpdate && (!editable || testSaved || updateParams?.skill_id === savedSkillId);

  const filtersContent = filters ? (
    <CardSection narrow top={!shady} flex>
      {filters}
    </CardSection>
  ) : undefined;

  return (
    <Box flexGrow={1} display="flex" flexDirection="column" position="relative">
      {filtersContent}
      <ReloadingOverlay reloading={reloading}>
        {(table && (
          <SkillsTable
              isEmployee={isEmployee}
              supervisor={supervisor}
              depersonalised={depersonalised}
              skills={skills}
              pending={loading}
              failed={failed}
              sortBy={sortBy}
              direction={direction}
              changeSort={changeSort}
              disabled={disabled || sortDisabled}
              updatingLevelId={updatingId}
              // TODO: withRemoveSuggested={withRemoveSuggested}
              // TODO: onRemoveClick={removeIcon || onItemRemove ? handleRemove : undefined}
              // TODO: withRemoveIcon={removeIcon === 'all'}
              onLevelClick={withLink || (withLink !== false && !isEmployee) && !readOnly ? undefined : handleClick}
              plain={plain}
              withExpectedLevel={withExpectedLevel}
          />
        )) || (failed || loading || !skills || size(skills) < 1
          ? (
            <Box
                className={clsx({
                  [topPadding]: !shady && filters && (failed || loading || !skills || !notFoundMessage),
                  [bottomPadding]: withPagination,
                  [shadyBg]: shady,
                  [dirtyBox]: dirty
                })}
            >
              {(failed && <FetchFailedAlert flat/>) ||
              ((loading || !skills) && <LoadingPlaceholder flat/>) || (
                <>
                  {!shady && filters && !notFoundMessage && !isNull(notFoundMessage) ? <Divider/> : undefined}
                  {isNull(notFoundMessage) ? undefined : (
                    <InlineAlert severity="info" message={notFoundMessage || 'skills.no_skills_found'} flat/>
                  )}
                </>
              )}
            </Box>
          ) : (
            <CardDeck shady={shady}>
              {map(skills, (skl /* , index */) => (
                <Fragment key={skl.id}>
                  <SkillCard
                      isEmployee={isEmployee}
                      skill={skl}
                      plain={plain}
                      small={small}
                      depersonalised={depersonalised}
                      withRemoveSuggested={withRemoveSuggested}
                      onRemoveClick={removeIcon || onItemRemove ? handleRemove : undefined}
                      withRemoveIcon={removeIcon === 'all'}
                      withExpectedLevel={withExpectedLevel}
                      withCourses={withCourses}
                      narrowGrid={narrowGrid}
                      onLevelClick={withLink || (withLink !== false && !isEmployee && !readOnly) ? undefined : handleClick}
                      // onTargetClick={handleTargetClick}
                      disabled={disabled}
                      pendingLevel={updatingId === skl.id}
                      // pendingTarget={updatingTargetId === skl.id}
                  />
                  {/* breakAfter === index + 1 && (
                    <Box width="100%" height="0"/>
                  ) */}
                </Fragment>
              ))}
            </CardDeck>
        ))}
        {withPagination ? (
          <CardSection narrow bottom={!shady} flex>
            {pagination}
          </CardSection>
        ) : pagination}
      </ReloadingOverlay>
      {skill ? (
        <SkillLevelDialog
            isOpen={open}
            skill={skill}
            plain={plain}
            defaultLevel={withExpectedLevel && depersonalised && manualOnly ? skill.expected_level as SkillLevel : undefined}
            readOnly={readOnly}
            editable={editable}
            confirmLabel={editable ? 'supervisor.button.save_changed_level' : undefined}
            removeLabel={editable ? 'supervisor.button.remove_skill' : undefined}
            employeeName={editable ? selectedEmployeeName : undefined}
            manualOnly={manualOnly}
            onUpdate={readOnly && !editable ? handleClose : handleLevelChange}
            onCancel={handleClose}
            onExited={handleExited}
            disabled={disabled}
            pending={updatePending}
        />
      ) : undefined}
      <ActionSnackbar
          severity="error"
          message="skill.level_update_error"
          open={
              updateFailed &&
              updateParams &&
              isPlainObject(updateParams) &&
              updateParams.skill_id >= 1 &&
              findIndex(skills, ['id', updateParams.skill_id]) >= 0 &&
              ownsFailedUpdate
            ? true : undefined
          }
      />
      {/* <ActionSnackbar
          severity="error"
          message="skill.target_update_error"
          open={
              targetFailed &&
              targetParams &&
              isPlainObject(targetParams) &&
              (targetParams.skill_id || 0) >= 1 &&
              findIndex(skills, ['id', targetParams.skill_id]) >= 0
            ? true : undefined
          }
      /> */}
      {dirty ? <ComputeUpdatesOverlay/> : undefined}
    </Box>
  );
};

export default memo(SkillsGrid);
